import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Check, ClipboardList, Inbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { followupsAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SCOPES = [
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'done', label: 'Completed' },
  { key: 'all', label: 'All' },
];

const TILES = [
  ['open', 'Open follow-ups'],
  ['overdue', 'Overdue'],
  ['due_this_week', 'Due this week'],
  ['owned_inquiries', 'Inquiries I own'],
];

export default function MyFollowupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scope, setScope] = useState('open');
  const [data, setData] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [viewUser, setViewUser] = useState('me');

  const load = useCallback(async () => {
    try {
      const response = await followupsAPI.mine({
        scope, user_id: viewUser === 'me' ? undefined : viewUser,
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load follow-ups');
    }
  }, [scope, viewUser]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.role === 'admin') usersAPI.directory().then((res) => setDirectory(res.data.users || [])).catch(() => {});
  }, [user]);

  const complete = async (item) => {
    try {
      await followupsAPI.update(item.id, { status: item.status === 'done' ? 'open' : 'done' });
      await load();
    } catch (error) { toast.error('Could not update the follow-up'); }
  };

  if (!data) {
    return <div className="flex min-h-[400px] items-center justify-center" data-testid="my-followups-loading"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="my-followups-page">
      <header className="flex flex-col gap-3 border-b pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">My follow-ups</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything assigned to {viewUser === 'me' ? 'you' : directory.find((member) => member.id === viewUser)?.name},
            plus the inquiries you are the owner of.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === 'admin' && (
            <Select value={viewUser} onValueChange={setViewUser}>
              <SelectTrigger className="w-[200px]" data-testid="followups-user-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Me</SelectItem>
                {directory.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-1">
            {SCOPES.map((item) => (
              <Button key={item.key} size="sm" variant={scope === item.key ? 'default' : 'outline'}
                onClick={() => setScope(item.key)} data-testid={`followups-scope-${item.key}`}>
                {item.label}
              </Button>
            ))}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {TILES.map(([key, label]) => (
          <div key={key} className="border bg-white px-4 py-3" data-testid={`followups-tile-${key}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
            <p className={`mt-1 font-mono text-2xl font-bold tabular-nums ${
              key === 'overdue' && data.summary.overdue > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
              {data.summary[key]}
            </p>
          </div>
        ))}
      </div>

      <section className="border bg-white" data-testid="followups-list-section">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <CalendarClock className="h-4 w-4 text-blue-600" />
          <h2 className="font-heading font-bold">Follow-ups</h2>
        </div>
        {data.items.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="followups-empty">
            <Inbox className="mx-auto mb-3 h-10 w-10 opacity-50" />
            <p>Nothing here — add follow-ups from any inquiry page.</p>
          </div>
        ) : (
          <div className="divide-y">
            {data.items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`my-followup-${item.id}`}>
                <Button size="icon" variant="ghost" onClick={() => complete(item)}
                  title={item.status === 'done' ? 'Reopen' : 'Mark done'}
                  data-testid={`my-followup-toggle-${item.id}`}>
                  <Check className={`h-4 w-4 ${item.status === 'done' ? 'text-emerald-600' : 'text-slate-400'}`} />
                </Button>
                <button type="button" className="font-mono text-xs font-semibold text-blue-700 underline"
                  onClick={() => navigate(`/inquiries/${item.inquiry_id}`)}>
                  {item.inquiry_number}
                </button>
                <span className="text-xs text-muted-foreground">{item.customer_name}</span>
                <span className={`flex-1 ${item.status === 'done' ? 'text-muted-foreground line-through' : 'font-medium'}`}>
                  {item.note}
                </span>
                {item.due_date && (
                  <Badge className={item.is_overdue ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'}>
                    due {item.due_date}{item.is_overdue ? ` · ${Math.abs(item.days_to_due)}d late` : ''}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{item.assignee_name}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border bg-white" data-testid="owned-inquiries-section">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <ClipboardList className="h-4 w-4 text-blue-600" />
          <h2 className="font-heading font-bold">Inquiries I own ({data.owned_inquiries.length})</h2>
        </div>
        {data.owned_inquiries.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="owned-inquiries-empty">
            No inquiries assigned to you yet — an admin assigns owners on the inquiry page.
          </p>
        ) : (
          <div className="divide-y">
            {data.owned_inquiries.slice(0, 25).map((inquiry) => (
              <button key={inquiry.id} type="button"
                className="flex w-full flex-wrap items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-blue-50/40"
                onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                data-testid={`owned-inquiry-${inquiry.inquiry_number}`}>
                <span className="font-mono text-xs font-semibold text-blue-700">{inquiry.inquiry_number}</span>
                <span className="flex-1 truncate text-sm">{inquiry.customer_name}</span>
                <Badge variant="outline">{(inquiry.status || '').replace(/_/g, ' ')}</Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(inquiry.created_at).toLocaleDateString('en-IN')}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
